import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Volume2, VolumeX, Play, Pause, RotateCcw, BookOpen,
  Star, Calendar, Trophy, Users, Heart, Sun, Moon,
  Mic, Copy, Download, Share, Eye, ChevronRight,
  Sparkles, Feather, Globe, Clock
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { languageAPI, vocabularyAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import AuthModal from '../components/AuthModal';
import useSubscription from '../hooks/useSubscription';
import SubscriptionGate from '../components/SubscriptionGate';

const LanguageRestoration = () => {
  const { isAuthenticated, user } = useAuth();
  const { isPaidUser } = useSubscription();
  const { toast } = useToast();
  const [tribalLanguages, setTribalLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('greetings');
  const [phrases, setPhrases] = useState([]);
  const [currentPhrase, setCurrentPhrase] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [practiceMode, setPracticeMode] = useState(false);
  const [dailyProgress, setDailyProgress] = useState({ learned: 0, practiced: 0, streak: 0 });
  const [userProgress, setUserProgress] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const audioRef = useRef(null);
  const synthRef = useRef(null);

  // Load initial data
  useEffect(() => {
    loadLanguagesAndCategories();
  }, []);

  // Load phrases when language or category changes
  useEffect(() => {
    if (selectedLanguage && selectedCategory) {
      loadPhrases();
    }
  }, [selectedLanguage, selectedCategory]);

  // Load user progress when authenticated
  useEffect(() => {
    if (isAuthenticated && selectedLanguage) {
      loadUserProgress();
    }
  }, [isAuthenticated, selectedLanguage]);

  const loadLanguagesAndCategories = async () => {
    try {
      setIsLoading(true);
      
      // Load languages from new vocabulary system
      const vocabularyLanguagesData = await vocabularyAPI.getLanguages();
      
      // Convert vocabulary API format to frontend format
      const formattedLanguages = vocabularyLanguagesData.map(lang => ({
        id: lang.code,
        name: `${lang.name} (${lang.native_name})`,
        nativeName: lang.native_name,
        speakers: `${lang.active_speakers}+`,
        region: lang.region,
        color: getLanguageColor(lang.code),
        sacred: true,
        family: lang.family,
        description: lang.description,
        vocabulary_count: lang.vocabulary_count
      }));
      
      // Add fallback languages if needed (from legacy language API)
      try {
        const legacyLanguagesData = await languageAPI.getLanguages();
        const existingCodes = new Set(formattedLanguages.map(lang => lang.id));
        
        const legacyLanguages = legacyLanguagesData
          .filter(lang => !existingCodes.has(lang.code))
          .map(lang => ({
            id: lang.code,
            name: `${lang.name} (${lang.native_name})`,
            nativeName: lang.native_name,
            speakers: `${lang.speakers_count}+`,
            region: lang.region,
            color: getLanguageColor(lang.code),
            sacred: true,
            family: lang.family.toLowerCase(),
            description: lang.description,
            vocabulary_count: 0
          }));
          
        formattedLanguages.push(...legacyLanguages);
      } catch (legacyError) {
        console.warn('Failed to load legacy languages:', legacyError);
      }
      
      // Remove duplicates by language code (keep first occurrence)
      const uniqueLanguages = [];
      const seenCodes = new Set();
      
      for (const lang of formattedLanguages) {
        if (!seenCodes.has(lang.id)) {
          seenCodes.add(lang.id);
          uniqueLanguages.push(lang);
        }
      }
      
      setTribalLanguages(uniqueLanguages);
      
      // Set first language as default
      if (uniqueLanguages.length > 0) {
        setSelectedLanguage(uniqueLanguages[0].id);
      }

      // Load categories from vocabulary system
      const vocabularyCategoriesData = await vocabularyAPI.getCategories();
      setCategories(vocabularyCategoriesData);
      
    } catch (error) {
      console.error('Failed to load languages and categories:', error);
      
      // Fallback to original language API if vocabulary API fails
      try {
        const fallbackLanguagesData = await languageAPI.getLanguages();
        const fallbackFormattedLanguages = fallbackLanguagesData.map(lang => ({
          id: lang.code,
          name: `${lang.name} (${lang.native_name})`,
          nativeName: lang.native_name,
          speakers: `${lang.speakers_count}+`,
          region: lang.region,
          color: getLanguageColor(lang.code),
          sacred: true,
          family: lang.family.toLowerCase(),
          description: lang.description,
          vocabulary_count: 0
        }));
        
        // Remove duplicates in fallback as well
        const uniqueFallbackLanguages = [];
        const seenCodes = new Set();
        
        for (const lang of fallbackFormattedLanguages) {
          if (!seenCodes.has(lang.id)) {
            seenCodes.add(lang.id);
            uniqueFallbackLanguages.push(lang);
          }
        }
        
        setTribalLanguages(uniqueFallbackLanguages);
        
        if (uniqueFallbackLanguages.length > 0) {
          setSelectedLanguage(uniqueFallbackLanguages[0].id);
        }
        
        const fallbackCategoriesData = await languageAPI.getCategories();
        setCategories(fallbackCategoriesData);
        
        toast({
          title: "Using Fallback Data",
          description: "Loaded basic language data. Some features may be limited.",
        });
      } catch (fallbackError) {
        toast({
          variant: "destructive",
          title: "Loading Error", 
          description: "Failed to load language data. Please refresh the page.",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadPhrases = async () => {
    console.log('Loading phrases for:', selectedLanguage, selectedCategory);
    
    try {
      // First try to load from vocabulary API
      const vocabularyPhrasesData = await vocabularyAPI.getPhrases(selectedLanguage, selectedCategory);
      console.log('Vocabulary API phrases loaded:', vocabularyPhrasesData);
      
      if (vocabularyPhrasesData && vocabularyPhrasesData.length > 0) {
        // Convert vocabulary API format to frontend phrase format
        const convertedPhrases = vocabularyPhrasesData.map(entry => ({
          id: entry.id,
          phrase: entry.native_text,
          phonetic: entry.phonetic || entry.pronunciation || "",
          pronunciation: entry.pronunciation || entry.phonetic || "",
          english: entry.english,
          context: entry.context || "",
          cultural: entry.cultural_context || "",
          difficulty: entry.difficulty || "beginner",
          sacred: entry.sacred || false,
          syllabary: entry.syllabary || null
        }));
        
        setPhrases(convertedPhrases);
        setCurrentPhrase(convertedPhrases[0]);
        toast({
          title: "Tribal Vocabulary Loaded",
          description: `Loaded ${convertedPhrases.length} authentic ${selectedCategory} phrases.`,
        });
        return;
      }
    } catch (error) {
      console.error('Failed to load phrases from vocabulary API:', error);
    }
    
    try {
      // Fallback to original language API
      const legacyPhrasesData = await languageAPI.getPhrases(selectedLanguage, selectedCategory);
      console.log('Legacy API phrases loaded:', legacyPhrasesData);
      
      if (legacyPhrasesData && legacyPhrasesData.length > 0) {
        // Convert legacy API format to frontend phrase format
        const convertedPhrases = legacyPhrasesData.map(phrase => ({
          id: phrase.id,
          phrase: phrase.native_phrase || phrase.text_native || phrase.phrase,
          phonetic: phrase.phonetic_spelling || phrase.text_phonetic || phrase.phonetic || "",
          pronunciation: phrase.pronunciation || phrase.phonetic_spelling || "",
          english: phrase.english_translation || phrase.text_english || phrase.english,
          context: phrase.cultural_context || phrase.context || "",
          cultural: phrase.cultural_context || "",
          difficulty: phrase.difficulty_level || phrase.difficulty || "beginner",
          sacred: phrase.sacred || false,
          syllabary: phrase.syllabary || null
        }));
        
        setPhrases(convertedPhrases);
        setCurrentPhrase(convertedPhrases[0]);
        toast({
          title: "Phrases Loaded",
          description: `Loaded ${convertedPhrases.length} phrases for ${selectedCategory}.`,
        });
        return;
      }
    } catch (error) {
      console.error('Failed to load phrases from legacy API:', error);
    }
    
    // Final fallback to mock data
    console.log('Using mock data for phrases');
    const mockData = getMockPhrases();
    
    if (mockData && mockData.length > 0) {
      setPhrases(mockData);
      setCurrentPhrase(mockData[0]);
      toast({
        title: "Sample Phrases Loaded",
        description: `Showing ${mockData.length} sample phrases for ${selectedCategory}.`,
      });
    } else {
      setPhrases([]);
      setCurrentPhrase(null);
      toast({
        variant: "destructive",
        title: "No Phrases Available",
        description: `No phrases found for ${selectedCategory} in ${selectedLanguage}.`,
      });
    }
  };

  const loadUserProgress = async () => {
    if (!isAuthenticated) return;
    
    try {
      const progressData = await languageAPI.getUserProgress();
      const languageProgress = progressData.find(p => p.language_code === selectedLanguage);
      setUserProgress(languageProgress);
      
      if (languageProgress) {
        setDailyProgress({
          learned: languageProgress.phrases_learned?.length || 0,
          practiced: languageProgress.phrases_practicing?.length || 0,
          streak: languageProgress.streak_days || 0
        });
      }
    } catch (error) {
      console.error('Failed to load user progress:', error);
    }
  };

  const startLearningLanguage = async (languageCode) => {
    if (!isAuthenticated) return;
    
    try {
      await languageAPI.startLearning({
        language_code: languageCode,
        daily_goal: 5
      });
      
      toast({
        title: "Language Learning Started!",
        description: `You've started learning ${tribalLanguages.find(l => l.id === languageCode)?.name}`,
      });
      
      loadUserProgress();
    } catch (error) {
      console.error('Failed to start learning:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to start language learning. Please try again.",
      });
    }
  };

  const markPhraseAsLearned = async (phraseId, learned = true) => {
    if (!isAuthenticated) return;
    
    try {
      await languageAPI.updateProgress(selectedLanguage, phraseId, learned);
      loadUserProgress();
      
      toast({
        title: learned ? "Phrase Learned!" : "Phrase Unmarked",
        description: learned ? "Great progress on your language journey!" : "Phrase removed from learned list",
      });
    } catch (error) {
      console.error('Failed to update progress:', error);
    }
  };

  const generateAudio = async (phraseId) => {
    try {
      await languageAPI.generateAudio(phraseId, {
        voice_preference: 'elder',
        speed: 'normal'
      });
      
      toast({
        title: "Audio Generated",
        description: "Pronunciation audio is being generated for this phrase.",
      });
    } catch (error) {
      console.error('Failed to generate audio:', error);
    }
  };

  const getLanguageColor = (code) => {
    const colors = {
      'chr': 'bg-red-100 text-red-800',
      'mik': 'bg-blue-100 text-blue-800', 
      'cho': 'bg-green-100 text-green-800',
      'sem': 'bg-yellow-100 text-yellow-800'
    };
    return colors[code] || 'bg-purple-100 text-purple-800';
  };

  const phraseCategories = [
    { id: 'greetings', name: 'Greetings & Farewells', icon: Users, description: 'Daily interactions and social connections' },
    { id: 'blessings', name: 'Blessings & Prayers', icon: Star, description: 'Sacred words for spiritual practice' },
    { id: 'ceremonial', name: 'Ceremonial Phrases', icon: Feather, description: 'Ritual and ceremony language' },
    { id: 'nature', name: 'Nature & Elements', icon: Sun, description: 'Words for the natural world' },
    { id: 'family', name: 'Family & Relations', icon: Heart, description: 'Kinship terms and family connections' },
    { id: 'numbers', name: 'Numbers & Time', icon: Clock, description: 'Counting and temporal expressions' }
  ];

  const mockPhrases = {
    cherokee: {
      greetings: [
        {
          id: 1,
          phrase: "ᏏᏲ",
          phonetic: "see-yo",
          pronunciation: "/ˈsi.jo/",
          english: "Hello",
          context: "General greeting used any time of day. One of the most commonly used Cherokee words.",
          cultural: "This greeting acknowledges the presence and spirit of another person. In Cherokee culture, proper greetings show respect and maintain harmony.",
          difficulty: "beginner",
          sacred: false
        },
        {
          id: 2,
          phrase: "ᎪᎱᏍᏗ",
          phonetic: "go-huu-s-di",
          pronunciation: "/ˈɡo.hũː.s.di/",
          english: "Good morning",
          context: "Traditional morning greeting, literally means 'it is morning'",
          cultural: "Acknowledging the morning connects us to the daily cycle and Creator's gift of a new day.",
          difficulty: "intermediate",
          sacred: true
        },
        {
          id: 3,
          phrase: "ᏙᎾᏓᎪᎲᏍᎦ",
          phonetic: "do-na-da-go-hv-s-ga",
          pronunciation: "/ˈdo.na.da.ɡo.hṽ̂.s.ɡa/",
          english: "Until we meet again",
          context: "Formal farewell expressing hope for future reunion",
          cultural: "This farewell acknowledges the eternal nature of relationships and spiritual connections.",
          difficulty: "advanced",
          sacred: true
        }
      ],
      blessings: [
        {
          id: 4,
          phrase: "ᏩᏙ ᎤᎾᏛᏅᎯ",
          phonetic: "wa-do u-na-dv-ne-hi",
          pronunciation: "/ˈwa.do ˈu.na.dṽ.ne.hi/",
          english: "Thank you Creator",
          context: "Sacred expression of gratitude to the Creator",
          cultural: "Acknowledging the Creator in all things is fundamental to Cherokee spirituality.",
          difficulty: "intermediate",
          sacred: true
        },
        {
          id: 5,
          phrase: "ᎠᏂᏣᎳᎩ ᎤᏂᏬᏂᎯᏍᏗ ᎠᎴ ᎤᏂᏨᏒᎾ",
          phonetic: "a-ni-tsa-la-gi u-ni-wo-ni-hi-s-di a-le u-ni-tsu-s-na",
          pronunciation: "/a.ni.t͡sa.la.ɡi u.ni.wo.ni.hi.s.di a.le u.ni.t͡su.s.na/",
          english: "Cherokee language and Cherokee people",
          context: "Blessing acknowledging the sacred connection between language and people",
          cultural: "Language is considered the heart of Cherokee identity and spiritual connection.",
          difficulty: "advanced",
          sacred: true
        }
      ],
      ceremonial: [
        {
          id: 6,
          phrase: "ᎦᏙ ᎠᎴ ᏅᏯ",
          phonetic: "ga-do a-le nv-ya",
          pronunciation: "/ˈɡa.do ˈa.le ˈnṽ.ja/",
          english: "Sacred and powerful",
          context: "Used in ceremony to acknowledge sacred power",
          cultural: "Recognizes the sacred power present in ceremonial space and ritual.",
          difficulty: "intermediate",
          sacred: true
        }
      ]
    },
    navajo: {
      greetings: [
        {
          id: 7,
          phrase: "Yá'át'ééh",
          phonetic: "yah-ah-tay",
          pronunciation: "/ˈjaː.ʔat.ʔeːh/",
          english: "Hello/It is good",
          context: "Universal Navajo greeting meaning 'it is good' - acknowledges the goodness in meeting",
          cultural: "This greeting reflects the Navajo philosophy that encounters should be positive and harmonious.",
          difficulty: "beginner",
          sacred: false
        },
        {
          id: 8,
          phrase: "Yá'át'ééh abíní",
          phonetic: "yah-ah-tay ah-bee-nee",
          pronunciation: "/ˈjaː.ʔat.ʔeːh a.ˈbiː.niː/",
          english: "Good morning",
          context: "Morning greeting combining 'it is good' with 'morning'",
          cultural: "Starting the day by acknowledging goodness sets intention for harmony.",
          difficulty: "beginner",
          sacred: false
        }
      ],
      blessings: [
        {
          id: 9,
          phrase: "Hózhó nahasdlíí'",
          phonetic: "ho-zho nah-has-dlee",
          pronunciation: "/ˈhoː.ʒoː na.has.dliːʔ/",
          english: "May you walk in beauty",
          context: "Traditional blessing wishing someone to live in harmony and balance",
          cultural: "Hózhó is the central concept in Navajo philosophy - living in balance with all creation.",
          difficulty: "intermediate",
          sacred: true
        }
      ]
    },
    yoruba: {
      greetings: [
        {
          id: 10,
          phrase: "Báwo ni",
          phonetic: "bah-wo nee",
          pronunciation: "/ˈba.wo niː/",
          english: "How are you?",
          context: "Common everyday greeting asking about someone's wellbeing",
          cultural: "Yoruba greetings often inquire about health and wellbeing, showing care for the whole person.",
          difficulty: "beginner",
          sacred: false
        },
        {
          id: 11,
          phrase: "Ẹ kú àárọ̀",
          phonetic: "eh koo ah-ah-raw",
          pronunciation: "/ɛ̃ kú àː.rɔ̀/",
          english: "Good morning",
          context: "Respectful morning greeting, literally 'well done for surviving the night'",
          cultural: "Acknowledges the blessing of waking up safely and the protection received during sleep.",
          difficulty: "intermediate",
          sacred: false
        }
      ],
      blessings: [
        {
          id: 12,
          phrase: "Asẹ",
          phonetic: "ah-shay",
          pronunciation: "/a.ʃɛ/",
          english: "So be it/Amen/Divine force",
          context: "Sacred word meaning divine life force, creative power, so be it",
          cultural: "Asẹ is the divine force that runs through all things - the power to make things happen.",
          difficulty: "beginner",
          sacred: true
        }
      ]
    }
  };

  // Initialize speech synthesis
  useEffect(() => {
    if ('speechSynthesis' in window) {
      synthRef.current = window.speechSynthesis;
    } else {
      console.warn('Speech synthesis not supported in this browser');
    }
  }, []);

  const playPhrase = (phrase) => {
    if (!('speechSynthesis' in window)) {
      toast({
        variant: "destructive",
        title: "Audio Not Supported",
        description: "Your browser doesn't support text-to-speech functionality.",
      });
      return;
    }

    try {
      // Cancel any currently playing speech
      if (synthRef.current) {
        synthRef.current.cancel();
      }
      
      // Create new utterance with the native phrase
      const utterance = new SpeechSynthesisUtterance(phrase.phrase);
      
      // Configure speech settings for better pronunciation
      utterance.rate = 0.6; // Slower for learning
      utterance.pitch = 1.0;
      utterance.volume = 1.0;
      
      // Try to find a more appropriate voice if available
      const voices = synthRef.current.getVoices();
      const preferredVoice = voices.find(voice => 
        voice.lang.includes('en') && voice.name.includes('Female')
      ) || voices.find(voice => voice.lang.includes('en')) || voices[0];
      
      if (preferredVoice) {
        utterance.voice = preferredVoice;
      }
      
      // Set up event handlers
      setIsPlaying(true);
      utterance.onstart = () => {
        console.log('Speech started for:', phrase.phrase);
      };
      utterance.onend = () => {
        console.log('Speech ended for:', phrase.phrase);
        setIsPlaying(false);
      };
      utterance.onerror = (event) => {
        console.error('Speech synthesis error:', event);
        setIsPlaying(false);
        toast({
          variant: "destructive",
          title: "Playback Error",
          description: "Unable to play pronunciation. Please try again.",
        });
      };
      
      // Speak the phrase
      synthRef.current.speak(utterance);
      
      // Show feedback to user
      toast({
        title: "Playing Pronunciation",
        description: `Listening to: "${phrase.english}"`,
      });
      
    } catch (error) {
      console.error('Error playing phrase:', error);
      setIsPlaying(false);
      toast({
        variant: "destructive",
        title: "Playback Error",
        description: "Failed to play pronunciation. Please try again.",
      });
    }
  };

  const stopAudio = () => {
    if (synthRef.current) {
      synthRef.current.cancel();
      setIsPlaying(false);
    }
  };

  const copyPhrase = async (phrase) => {
    try {
      const textToCopy = `${phrase.phrase} (${phrase.phonetic}) - ${phrase.english}`;
      
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(textToCopy);
        toast({
          title: "Copied to Clipboard",
          description: `"${phrase.english}" copied successfully!`,
        });
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = textToCopy;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        
        toast({
          title: "Copied to Clipboard",
          description: `"${phrase.english}" copied successfully!`,
        });
      }
    } catch (error) {
      console.error('Failed to copy phrase:', error);
      toast({
        variant: "destructive",
        title: "Copy Failed",
        description: "Unable to copy phrase to clipboard.",
      });
    }
  };

  const markAsLearned = async (phraseId) => {
    if (isAuthenticated) {
      await markPhraseAsLearned(phraseId, true);
    } else {
      // Update local state for unauthenticated users
      setDailyProgress(prev => ({
        ...prev,
        learned: prev.learned + 1
      }));
    }
  };

  const getCurrentPhrases = () => {
    // If we have phrases from API, use them
    if (phrases && phrases.length > 0) {
      return phrases;
    }
    
    // Fallback to mock data for demonstration/testing
    const mockData = getMockPhrases();
    if (mockData && mockData.length > 0) {
      return mockData;
    }
    
    // Return empty array if nothing available
    return [];
  };

  // Helper function to get mock phrases for fallback
  const getMockPhrases = () => {
    // Map selected language to mock data
    const languageMap = {
      'chr': 'cherokee',
      'cherokee': 'cherokee',
      'nav': 'navajo', 
      'navajo': 'navajo',
      'yor': 'yoruba',
      'yoruba': 'yoruba'
    };
    
    const mockLanguage = languageMap[selectedLanguage] || 'cherokee';
    
    // Return mock phrases for the selected category
    return mockPhrases[mockLanguage]?.[selectedCategory] || [];
  };

  const selectedLanguageData = tribalLanguages.find(lang => lang.id === selectedLanguage);



  return (
    <SubscriptionGate 
      feature="language_restoration"
      title="Full Language Restoration Tool"
      description="Reconnect with your ancestral tongues through traditional greetings, sacred blessings, and cultural teachings"
    >
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-red-50">
      {/* Hero Banner */}
      <section className="relative py-16 lg:py-24 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-amber-100/80 via-orange-100/60 to-red-100/80"></div>
          {/* Floating language elements */}
          <div className="absolute top-20 left-20 w-6 h-6 text-amber-500 opacity-60 animate-pulse">ᏣᎳᎩ</div>
          <div className="absolute top-32 right-32 w-4 h-4 text-orange-500 opacity-60 animate-pulse">Diné</div>
          <div className="absolute bottom-40 left-1/3 w-5 h-5 text-red-500 opacity-60 animate-pulse">Lakȟól</div>
          <div className="absolute bottom-20 right-20 w-3 h-3 text-amber-500 opacity-60 animate-pulse">ᐃᓄᒃ</div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Language Restoration
              <br />
              <span className="text-orange-700">Reclaim Your Ancestral Voice</span>
            </h1>
            <p className="text-xl lg:text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed mb-8">
              Reconnect with your ancestral tongues through traditional greetings, sacred blessings, 
              and ceremonial phrases. Restore the speech that colonial powers tried to silence.
            </p>
          </div>

          {/* Language Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="text-center p-4 bg-white/80 rounded-lg backdrop-blur-sm">
              <Globe className="w-8 h-8 text-orange-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900">24</div>
              <div className="text-sm text-gray-600">Sacred Languages</div>
            </div>
            <div className="text-center p-4 bg-white/80 rounded-lg backdrop-blur-sm">
              <BookOpen className="w-8 h-8 text-orange-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900">4,700+</div>
              <div className="text-sm text-gray-600">Phrases & Blessings</div>
            </div>
            <div className="text-center p-4 bg-white/80 rounded-lg backdrop-blur-sm">
              <Volume2 className="w-8 h-8 text-orange-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900">Audio</div>
              <div className="text-sm text-gray-600">Pronunciation</div>
            </div>
            <div className="text-center p-4 bg-white/80 rounded-lg backdrop-blur-sm">
              <Heart className="w-8 h-8 text-orange-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900">Daily</div>
              <div className="text-sm text-gray-600">Practice Ritual</div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Language Learning Interface */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {!isAuthenticated ? (
            <Card className="max-w-2xl mx-auto bg-white/90 backdrop-blur-sm shadow-lg">
              <CardContent className="p-6 text-center">
                <BookOpen className="w-12 h-12 text-orange-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Begin Your Language Journey
                </h3>
                <p className="text-gray-600 mb-4">
                  Sign in to access sacred phrases, pronunciation guides, and daily practice rituals 
                  for restoring your ancestral voice.
                </p>
                <AuthModal>
                  <Button className="bg-orange-700 hover:bg-orange-800 text-white px-6 py-2">
                    Start Language Restoration
                  </Button>
                </AuthModal>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {/* Language Toolbar - Now at top */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Selected Language Card */}
                {selectedLanguageData && (
                  <Card className="bg-white shadow-lg">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center">
                        <Globe className="w-4 h-4 mr-2" />
                        Current Language
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div>
                          <h3 className="font-semibold text-lg">{selectedLanguageData.name}</h3>
                          <p className="text-sm text-gray-600">{selectedLanguageData.nativeName}</p>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span>Region:</span>
                          <span className="text-gray-600 text-xs">{selectedLanguageData.region}</span>
                        </div>
                        {selectedLanguageData.sacred && (
                          <Badge className="w-full justify-center bg-amber-600 text-xs">
                            <Star className="w-3 h-3 mr-1" />
                            Sacred Language
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Daily Progress */}
                <Card className="bg-gradient-to-br from-green-50 to-blue-50 shadow-lg">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center">
                      <Calendar className="w-4 h-4 mr-2" />
                      Today's Practice
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Phrases Learned:</span>
                      <Badge className="bg-green-600">{dailyProgress.learned}</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Times Practiced:</span>
                      <Badge className="bg-blue-600">{dailyProgress.practiced}</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Daily Streak:</span>
                      <Badge className="bg-orange-600">{dailyProgress.streak} days</Badge>
                    </div>
                  </CardContent>
                </Card>

                {/* Language Selector Dropdown */}
                <Card className="bg-white shadow-lg">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Select Language</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <select
                        value={selectedLanguage}
                        onChange={(e) => setSelectedLanguage(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      >
                        <option value="">Choose a language...</option>
                        {tribalLanguages.map((language) => (
                          <option key={language.id} value={language.id}>
                            {language.name}
                          </option>
                        ))}
                      </select>
                      {selectedLanguageData && (
                        <div className="text-xs text-gray-600 mt-2">
                          <span className="font-medium">Speakers:</span> {selectedLanguageData.speakers}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Main Content Area */}
              <div className="space-y-6">
                {/* Category Tabs */}
                <Card className="bg-white shadow-lg">
                  <CardContent className="p-3 sm:p-6">
                    <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
                      <TabsList className="flex flex-wrap justify-center gap-1 h-auto p-1 sm:grid sm:w-full sm:grid-cols-6">
                        {phraseCategories.map((category) => {
                          const Icon = category.icon;
                          return (
                            <TabsTrigger key={category.id} value={category.id} className="flex items-center px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm">
                              <Icon className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                              <span className="hidden xs:inline sm:inline">{category.name.split(' ')[0]}</span>
                            </TabsTrigger>
                          );
                        })}
                      </TabsList>
                    </Tabs>

                    {/* Category Description */}
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <h3 className="font-semibold text-lg mb-2">
                        {phraseCategories.find(c => c.id === selectedCategory)?.name}
                      </h3>
                      <p className="text-gray-600 text-sm">
                        {phraseCategories.find(c => c.id === selectedCategory)?.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Phrases List */}
                <div className="space-y-4">
                  {getCurrentPhrases().map((phrase) => (
                    <Card key={phrase.id} className="bg-white shadow-lg hover:shadow-xl transition-shadow">
                      <CardContent className="p-6">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                          {/* Phrase Section */}
                          <div className="lg:col-span-1">
                            <div className="space-y-3">
                              <div>
                                <div className="text-2xl font-bold text-gray-900 mb-1">
                                  {phrase.phrase}
                                </div>
                                <div className="text-lg text-gray-600 mb-1">
                                  {phrase.phonetic}
                                </div>
                                <div className="text-sm text-gray-500 font-mono">
                                  {phrase.pronunciation}
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-2">
                                {phrase.sacred && (
                                  <Badge className="bg-amber-600">
                                    <Star className="w-3 h-3 mr-1" />
                                    Sacred
                                  </Badge>
                                )}
                                <Badge variant="outline" className={
                                  phrase.difficulty === 'beginner' ? 'text-green-700 border-green-300' :
                                  phrase.difficulty === 'intermediate' ? 'text-yellow-700 border-yellow-300' :
                                  'text-red-700 border-red-300'
                                }>
                                  {phrase.difficulty}
                                </Badge>
                              </div>

                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => playPhrase(phrase)}
                                  disabled={isPlaying}
                                  className="bg-orange-600 hover:bg-orange-700"
                                >
                                  {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => copyPhrase(phrase)}
                                >
                                  <Copy className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => markAsLearned(phrase.id)}
                                >
                                  <Trophy className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>

                          {/* Translation & Context */}
                          <div className="lg:col-span-2">
                            <div className="space-y-4">
                              <div>
                                <h4 className="font-semibold text-lg text-gray-900 mb-2">
                                  "{phrase.english}"
                                </h4>
                                <p className="text-gray-700">
                                  <strong>Context:</strong> {phrase.context}
                                </p>
                              </div>

                              <div className="p-4 bg-amber-50 rounded-lg border-l-4 border-amber-400">
                                <h5 className="font-semibold text-amber-800 mb-2">Cultural Significance</h5>
                                <p className="text-amber-700 text-sm">{phrase.cultural}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Daily practice ritual footer removed per user request */}

                {/* Empty State for Languages */}
                {getCurrentPhrases().length === 0 && (
                  <Card className="bg-white shadow-lg">
                    <CardContent className="p-12 text-center">
                      <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        Coming Soon
                      </h3>
                      <p className="text-gray-600">
                        We're working to add more phrases for {selectedLanguageData?.name}. 
                        Check back soon for updates to this sacred language collection.
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
      </div>
    </SubscriptionGate>
  );
};

export default LanguageRestoration;